/**
 * One ticket on a queue: the stage pie, the key, the title over its state line
 * (`STAGE · sentence`), the age, and the verb its group opens it with. A
 * pull request review nobody filed a ticket for wears Proctor's medallion
 * where the pie would be. Rows never commit: a click opens the sheet. A row
 * that closed with no work landed reads CLOSED, not DONE; an archived row,
 * which only a search prints, says when it was archived.
 *
 * A split's tickets print indented under their parent's row (YAG-1113),
 * each with its own stage word and its part: "part 3 of 3 · after part 2
 * merges" (`TicketEntry`).
 */
import type { WorkerSummary } from "@yagni/shared";
import { Row } from "../queue/index.js";
import { StagePie, STAGE_HUE } from "../ui/stagePie.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import { CODE_REVIEW_SLUG } from "../workers/crafts.js";
import { captureUiError } from "../sentry.js";
import type { StageOwnerLookup } from "../work/useWorkerLookup.js";
import { splitPartLine, type SplitPartPlace } from "../work/splitPlace.js";
import { driverOf, rowStageItemOf, type PipelineRow } from "../work/workViews.js";
import { pipelineStageOf, stageLabelOf, stagePieOf } from "../work/workStage.js";
import {
  closedRowWords,
  isStandaloneReview,
  rowKeyOf,
  rowVerbOf,
  rowWhenOf,
  ticketSentenceOf,
  type QueueGroup,
  type QueuePart,
} from "./queueGroups.js";

export interface TicketRowProps {
  row: PipelineRow;
  group: QueueGroup;
  workerOf?: StageOwnerLookup;
  viewerUserId: string | null;
  now: number;
  sel: boolean;
  onOpen: (id: string) => void;
  /** The ticket's archivedAt, for a row a search found in the archive. */
  archived?: string | null;
  /** A split's ticket under its parent's row: its part leads its line. */
  place?: SplitPartPlace;
}

/** Bad archive dates already reported, so a re-render does not report one again. */
const reportedBadDays = new Set<string>();

/**
 * "Oct 15": the day a ticket was archived. A date that does not parse prints
 * no day (the row still says "archived") and is reported once, so bad data
 * shows up in Sentry instead of hiding.
 */
export function archivedDay(iso: string, ticket: { id: string; key: string }): string {
  const at = new Date(iso);
  if (!Number.isNaN(at.getTime()))
    return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (!reportedBadDays.has(iso)) {
    reportedBadDays.add(iso);
    captureUiError(
      new Error("archived ticket has an unreadable archivedAt; the row prints no day"),
      {
        area: "Teams",
        operation: "read_archived_day",
        severity: "warning",
        workItemId: ticket.id,
        ticketKey: ticket.key,
        archivedAt: iso,
      },
    );
  }
  return "";
}

function Medallion({ worker }: { worker: WorkerSummary | null }) {
  if (!worker) return <span aria-hidden />;
  return (
    <WorkerMark avatar={worker.avatar} name={worker.displayName} slug={worker.slug} size="sm" />
  );
}

export function TicketRow({
  row,
  group,
  workerOf,
  viewerUserId,
  now,
  sel,
  onOpen,
  archived,
  place,
}: TicketRowProps) {
  const driver = driverOf(row);
  // The ticket's stage names the row, even while Fletcher tests its pull request.
  const stageItem = rowStageItemOf(row);
  const stage = pipelineStageOf(stageItem);
  const pie = stagePieOf(driver).mode;
  // A check's results that wait on a person wear the waiting ring, as an ask does.
  const mode = group === "needs" && pie === "past" ? "wait" : pie;
  const glyph = isStandaloneReview(row) ? (
    <Medallion worker={workerOf?.(CODE_REVIEW_SLUG) ?? null} />
  ) : (
    <StagePie stage={stage} mode={mode} size={26} />
  );
  const sentence = ticketSentenceOf(row, workerOf, viewerUserId);
  const when = rowWhenOf(row, group, now);
  const closed = closedRowWords(driver, sentence);
  const own = closed?.state ?? sentence;
  const text = place ? splitPartLine(place, own) : own;
  // A split's ticket that asks a person shows its verb, as the asks around it do.
  const asks = place != null && (group === "needs" || group === "ready");
  const day = archived
    ? archivedDay(archived, { id: row.anchor.id, key: rowKeyOf(row.anchor) })
    : "";
  const state = archived ? (
    <>
      {text}
      {text && <span className="sep">·</span>}
      <span className="arch">archived{day && ` ${day}`}</span>
    </>
  ) : (
    text
  );
  return (
    <Row
      glyph={glyph}
      rowKey={rowKeyOf(row.anchor)}
      title={row.anchor.title}
      tag={closed?.tag ?? { label: stageLabelOf(stageItem), color: STAGE_HUE[stage] }}
      state={state}
      when={
        when.text ? (
          <time dateTime={when.iso} title={when.title || undefined}>
            {when.text}
          </time>
        ) : null
      }
      verb={archived ? "Open" : rowVerbOf(row, group)}
      sel={sel}
      onOpen={() => onOpen(row.anchor.id)}
      className={asks ? "asks" : undefined}
    />
  );
}

export interface TicketEntryProps extends Omit<TicketRowProps, "sel"> {
  /** A split's tickets under their parent's row, by the parent's id (`QueueGroupRows.parts`). */
  parts: ReadonlyMap<string, readonly QueuePart[]>;
  isSelected: (row: PipelineRow) => boolean;
}

/**
 * One row of a queue and, under a split's parent, the tickets of the split,
 * indented, in split order (YAG-1113). Each ticket keeps its own verb: the
 * parent's row only opens the parent.
 */
export function TicketEntry({ parts, isSelected, ...props }: TicketEntryProps) {
  const under = parts.get(props.row.anchor.id) ?? [];
  return (
    <>
      <TicketRow {...props} sel={isSelected(props.row)} />
      {under.length > 0 && (
        <div
          className="parts"
          role="group"
          aria-label={`Tickets split from ${rowKeyOf(props.row.anchor)}`}
        >
          {under.map((part) => (
            <TicketEntry
              {...props}
              key={part.row.anchor.id}
              row={part.row}
              group={part.group}
              place={part.place}
              parts={parts}
              isSelected={isSelected}
            />
          ))}
        </div>
      )}
    </>
  );
}
